/**
 * Created Sprint: Sprint 1 - Monorepo Skeleton
 * Last contract-affecting Sprint: hotfix-sw-update-white-screen — FCE-SWUPDATE-INITEVAL-001: entry-level chunk-load self-heal (window 'vite:preloadError' + a try/catch around the startup dynamic imports → swRecovery.selfHealChunkLoadFailure) so a returning user on a stale/defective SW OR a stale edge cache that serves app-shell HTML for a new deploy's hashed chunk auto-recovers instead of white-screening (OBS-156). Edge-aware hardening (Owner 2026-07-02 §1.8, opts b+c): the self-heal is a BOUNDED backoff — plain reloads after ~3s/6s/10s (SW/precache preserved, to let a short edge-cache propagation window converge), then one last-resort unregister-SW+reload, then give up (surface, no reload storm). Counter is cleared after a clean mount so a future deploy heals fresh. Prior: Sprint 23+ critical priority audit session Path B (D) F4 sequence serialization + OBS-101 SW prepare-for-update cohesive dedicated session (FCE-S5-S6-P23-006) — swLifecycle handler registration at app startup per Y7 main.tsx direct invocation baseline strict
 * Freeze status: partially_frozen
 * Purpose: Application entry point.
 *   Stale Supabase auth lock cleanup runs before ANY app module is loaded,
 *   preventing multi-minute hang from orphaned navigator.locks.
 *   i18n, themes, and typography are pre-loaded as side-effect imports.
 */
import "./lib/i18n";
import "./styles/themes.css";
import "./styles/typography.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { selfHealChunkLoadFailure, clearRecoveryFlag } from "./lib/swRecovery";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("[Kiroku] #root element not found in index.html");
}

// OBS-126 Phase 2 (FCE-S26-SAVEDVIEWSRETIRE-001): one-time purge of the retired
// "Saved Views" localStorage residue. savedViewsStore was removed (zero consumer,
// dormant since the Saved Views UI removal); this clears any orphaned key left on
// user devices. Additive boot cleanup (§11.2) — safe to drop in a future sprint.
try {
  localStorage.removeItem("kiroku_saved_views");
} catch {
  // localStorage unavailable (private mode / disabled) — non-critical.
}

// Supabase Auth's createClient uses navigator.locks internally to
// serialise access to the auth token in localStorage. If a previous
// browser tab closed without releasing this lock (e.g. crash, force
// close, sleep), the lock becomes orphaned and createClient blocks
// for minutes waiting to acquire it.
//
// Fix: before loading any module that imports supabase.ts, we steal
// the specific lock name and immediately release it.
async function ensureAuthLockFree(): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.locks) return;

  // The lock name follows Supabase's pattern: lock:sb-{ref}-auth-token
  // We need to find the exact name since it includes the project ref.
  try {
    const state = await navigator.locks.query();
    const authLockNames = new Set(
      [...(state.held ?? []), ...(state.pending ?? [])]
        .filter(
          (l) =>
            typeof l.name === "string" &&
            l.name.startsWith("lock:sb-") &&
            l.name.endsWith("-auth-token"),
        )
        .map((l) => l.name as string),
    );

    if (authLockNames.size > 0) {
      // Orphaned or queued auth lock(s) found — steal and immediately release
      for (const lockName of authLockNames) {
        await navigator.locks.request(lockName, { steal: true }, () =>
          Promise.resolve(),
        );
      }

      // Give the event loop one tick to fully propagate the release
      await new Promise((r) => setTimeout(r, 0));
    }
  } catch {
    // locks.query() or steal failed — continue anyway
  }
}

// FCE-SWUPDATE-INITEVAL-001 (OBS-156) — entry-level chunk-load self-heal. Vite
// dispatches 'vite:preloadError' on window when a dynamically-imported chunk fails
// to load; a returning user on a stale/defective SW OR a stale edge cache gets
// app-shell HTML (text/html) for a new deploy's hashed chunk → module load fails →
// white screen. Registered here at entry top-level (the entry chunk still loads even
// when the lazy chunks fail) so recovery runs: preventDefault() suppresses the
// uncaught throw, then selfHealChunkLoadFailure() runs the bounded edge-aware backoff
// (plain reloads at ~3s/6s/10s with the SW/precache preserved → last-resort
// unregister-SW+reload → give up), reloading into a proven-clean load once the fault
// clears, and bounded so it never becomes a reload storm.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  void selfHealChunkLoadFailure();
});

(async () => {
  await ensureAuthLockFree();
  try {
    // FCE-S5-S6-P23-006 Part 5 — SW prepare-for-update handler registration at
    // app startup per hard-rules §4 L213-214 紀律 strict + Y7 main.tsx direct
    // invocation baseline strict. Earliest registration site ensures SW
    // activate events emit BEFORE React mount race window strict. Registration
    // is idempotent + capability-detected per swLifecycle.ts module strict.
    const { registerSwLifecycleHandler } = await import("./lib/swLifecycle");
    registerSwLifecycleHandler();
    const { App } = await import("./app/App");
    createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
    // Confirmed-clean mount: clear the self-heal attempt counter so a FUTURE deploy's
    // legit chunk failure heals from scratch (FCE-SWUPDATE-INITEVAL-001).
    clearRecoveryFlag();
  } catch (err) {
    // A startup dynamic import (swLifecycle / App / their deps) failed — most
    // likely the stale-SW text/html-for-chunk case that 'vite:preloadError' may
    // not always cover. Attempt the same guarded self-heal.
    console.warn(
      "[Kiroku] startup import failed — attempting SW self-heal recovery:",
      err,
    );
    void selfHealChunkLoadFailure();
  }
})();
